import { Directive, ElementRef, Input, HostListener } from '@angular/core';
import { AbstractControl, ValidationErrors, Validator } from '@angular/forms';

export type DocumentType = 'cpf' | 'cnpj' | 'caceal' | 'both';

@Directive()
export abstract class BaseDocumentMaskDirective implements Validator {
  @Input() type: DocumentType = 'both';

  constructor(protected el: ElementRef) {}

  // Abstract methods to be implemented by subclasses
  protected abstract validateDocument(value: string): boolean;
  protected abstract formatDocument(value: string): string;
  protected abstract getMaxLength(): number;

  // Common validation method
  validate(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.replace(/\D/g, '') || '';
    const isValid = this.validateDocument(value);
    return isValid ? null : { invalidFormat: true };
  }

  // Common input handler
  @HostListener('input', ['$event'])
  onInputChange(event: any): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, '');
    
    // Apply max length based on type
    const maxLength = this.getMaxLength();
    if (value.length > maxLength) {
      value = value.substring(0, maxLength);
    }

    // Format the value
    input.value = this.formatDocument(value);
  }

  // Common formatting and validation methods
  protected formatCaceal(caceal: string): string {
    if (caceal.length < 8) {
      return caceal;
    } else if (caceal.length === 8) {
      return `${caceal.slice(0, 2)}.${caceal.slice(2, 5)}.${caceal.slice(5, 8)}`;
    } else {
      return `${caceal.slice(0, 2)}.${caceal.slice(2, 5)}.${caceal.slice(5, 8)}-${caceal.slice(8, 9)}`;
    }
  }

  protected formatCNPJ(cnpj: string): string {
    if (cnpj.length < 7) {
      return cnpj;
    } else if (cnpj.length <= 8) {
      return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5)}`;
    } else if (cnpj.length <= 12) {
      return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8)}`;
    } else {
      return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(12, 14)}`;
    }
  }

  protected isValidCNPJ(cnpj: string): boolean {
    return cnpj.length === 14 && /^[0-9]{14}$/.test(cnpj);
  }

  protected isValidCACEAL(caceal: string): boolean {
    return (caceal.length === 9 && /^[0-9]{9}$/.test(caceal) || 
            caceal.length === 8 && /^[0-9]{8}$/.test(caceal));
  }
}
